import React from "react";
import { newColumnConfig, newColumnValueWithNumberComponent } from "../../TableList";
import { Link } from "react-router-dom";
import moment from "moment";
import { formatCurrency, getActualRole, getDateWithLocale, Roles } from "../../../../util";
import UserCommissionValue from "../UserCommissionValue";
import LeadershipCommissionList from "../LeadershipCommissionList";
import UserLink from "../UserLink";

export const useContractReportColumns = ({ currentUser, intl, classes, customerRoutePrefix = "/customer-detail" }) => {
  const isCurrentUserAnAgent = getActualRole(currentUser) === Roles.Agent;
  const shouldDisableAgentOrManagerLink = React.useCallback((data) => data?.agent.id === currentUser.id || isCurrentUserAnAgent, [currentUser.id, isCurrentUserAnAgent]);

  return React.useMemo(
    () => [
      {
        name: "contractId",
        label: "LCContract",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "LCContract",
            field: "contractId",
            type: "numeric",
            align: "center",
            render: (rowData) => (
              <Link
                to={`/userContract/${rowData.contractId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {rowData.contractId}
              </Link>
            ),
          }),
        exportValue: (rowData) => rowData.contractId,
      },
      {
        name: "customer",
        label: "app.customer",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.customer",
            field: "clientName",
            type: "string",
            align: "left",
            render: (rowData) => (
              <Link
                to={`${customerRoutePrefix}/${rowData.clientId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {rowData.clientName} <span className={classes.secondary}>{`(${rowData.clientId})`}</span>
              </Link>
            ),
          }),
        exportValue: (rowData) => `${rowData.clientName} (${rowData.clientId})`,
      },
      {
        name: "agent",
        label: "app.agent",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.agent",
            field: "agentName",
            type: "string",
            align: "left",
            render: (rowData) => (
              <UserLink
                user={rowData?.agent}
                currentUserId={currentUser.id}
                className={classes.link}
                secondaryClassName={classes.secondary}
                disableLink={shouldDisableAgentOrManagerLink(rowData)}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }),
        exportValue: (rowData) => `${rowData.agent.id} (${rowData.agent.id})`,
      },
      {
        name: "manager",
        label: "app.report.hierarchical.userInfo.manager",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.report.hierarchical.userInfo.manager",
            field: "managerName",
            type: "string",
            align: "left",
            render: (rowData) => (
              <UserLink
                user={rowData?.manager}
                currentUserId={currentUser.id}
                className={classes.link}
                secondaryClassName={classes.secondary}
                disableLink={shouldDisableAgentOrManagerLink(rowData)}
                emptyLabel=" - "
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }),
        exportValue: (rowData) => (rowData?.manager?.id ? `${rowData.manager.name} (${rowData.manager.id})` : " - "),
      },
      {
        name: "amount",
        label: "app.aport.value",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.aport.value", "amount", null, currentUser.hideValues, false, "userTaxResidence"),
        exportValue: (rowData) => formatCurrency({ value: rowData.amount, userLocale: currentUser.locale }),
      },
      {
        name: "previsionAmount",
        label: "app.estimatedAmount",
        enable: false,
        definition: () => newColumnValueWithNumberComponent("app.estimatedAmount", "previsionAmount", null, currentUser.hideValues, false, "userTaxResidence"),
        exportValue: (rowData) => formatCurrency({ value: rowData.previsionAmount, userLocale: currentUser.locale }),
      },
      {
        name: "contractType",
        label: "app.modality",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.modality",
            field: "contractType",
            type: "string",
            align: "center",
            render: (rowData) => `${intl.formatMessage({ id: `app.enuns.${rowData.contractType}` })}`,
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.enuns.${rowData.contractType}` })}`,
      },
      {
        name: "contractDuration",
        label: "app.duration",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.duration",
            field: "contractDuration",
            type: "string",
            align: "center",
            render: (rowData) => `${intl.formatMessage({ id: `app.durationInMonth.value` }, { months: rowData.contractDuration })}`,
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.durationInMonth.value` }, { months: rowData.contractDuration })}`,
      },
      {
        name: "createdAt",
        label: "app.createdAt",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.createdAt",
            field: "createdAt",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.createdAt ? moment(rowData.createdAt).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => getDateWithLocale(rowData.createdAt, currentUser.locale),
      },
      {
        name: "initialDate",
        label: "app.date.active",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.date.active",
            field: "initialDate",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.initialDate ? moment(rowData.initialDate).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => getDateWithLocale(rowData.initialDate, currentUser.locale),
      },
      {
        name: "dueDate",
        label: "app.contract.column.date.DUE_DATE",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.contract.column.date.DUE_DATE",
            field: "dueDate",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.dueDate ? moment(rowData.dueDate).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => getDateWithLocale(rowData.dueDate, currentUser.locale),
      },
      {
        name: "archiveDate",
        label: "app.contract.column.date.ARCHIVE_DATE",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.contract.column.date.ARCHIVE_DATE",
            field: "archiveDate",
            type: "date",
            align: "center",
            render: (rowData) => (rowData?.archiveDate ? moment(rowData.archiveDate).format("DD/MM/YYYY") : "N/D"),
          }),
        exportValue: (rowData) => getDateWithLocale(rowData.archiveDate, currentUser.locale),
      },
      {
        name: "contractSituation",
        label: "app.situation",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.situation",
            field: "contractSituation",
            type: "string",
            align: "center",
            render: (rowData) => `${intl.formatMessage({ id: `app.enuns.${rowData.contractSituation}` })}`,
          }),
        exportValue: (rowData) => `${intl.formatMessage({ id: `app.enuns.${rowData.contractSituation}` })}`,
      },
      {
        name: "yieldTotal",
        label: "app.addition",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.addition", "yieldTotal", null, currentUser.hideValues, false, "userTaxResidence"),
        exportValue: (rowData) => formatCurrency({ value: rowData.yieldTotal, userLocale: currentUser.locale }),
      },
      {
        name: "averageMonthlyYield",
        label: "app.addition.avg",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.addition.avg", "averageMonthlyYield", null, currentUser.hideValues, false, "userTaxResidence"),
        exportValue: (rowData) => formatCurrency({ value: rowData.averageMonthlyYield, userLocale: currentUser.locale }),
      },
      {
        name: "agentAmount",
        label: "app.commission.type.BONUS",
        enable: true,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS",
            field: "agentAmount",
            type: "string",
            align: "center",
            render: (rowData) => (
              <UserCommissionValue
                userId={rowData.agent.id}
                amount={rowData.agentAmount}
              />
            ),
          }),
        exportValue: (rowData) => formatCurrency({ value: rowData.agentAmount, userLocale: currentUser.locale }),
      },
      {
        name: "managerAmount",
        label: "app.commission.type.BONUS_MANAGER",
        enable: true,
        exclusive: true,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS_MANAGER",
            field: "managerAmount",
            type: "string",
            align: "center",
            render: (rowData) => {
              if (!rowData?.manager) return " - ";
              return (
                <UserCommissionValue
                  userId={rowData.manager.id}
                  amount={rowData.managerAmount}
                />
              );
            },
          }),
        exportValue: (rowData) => formatCurrency({ value: rowData.managerAmount, userLocale: currentUser.locale }),
      },
      {
        name: "managerSplitAmount",
        label: "app.commission.type.BONUS_MANAGER_SPLIT",
        enable: true,
        exclusive: true,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS_MANAGER_SPLIT",
            field: "managerSplitAmount",
            type: "string",
            align: "center",
            render: (rowData) => {
              if (!rowData?.manager) return " - ";
              return (
                <UserCommissionValue
                  userId={rowData.manager.id}
                  amount={rowData.managerSplitAmount}
                />
              );
            },
          }),
        exportValue: (rowData) => formatCurrency({ value: rowData.managerSplitAmount, userLocale: currentUser.locale }),
      },
      {
        name: "recommenderAmount",
        label: "app.commission.type.BONUS_RECOMMENDANT",
        enable: true,
        exclusive: true,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS_RECOMMENDANT",
            field: "recommenderAmount",
            type: "string",
            align: "center",
            render: (rowData) => {
              if (!rowData?.recommender) return " - ";
              return (
                <UserCommissionValue
                  userId={rowData.recommender.id}
                  amount={rowData.recommenderAmount}
                />
              );
            },
          }),
        exportValue: (rowData) => formatCurrency({ value: rowData.recommenderAmount, userLocale: currentUser.locale }),
      },
      {
        name: "splitAmount",
        label: "app.commission.type.BONUS_SPLIT",
        enable: true,
        exclusive: true,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS_SPLIT",
            field: "splitAmount",
            type: "string",
            align: "center",
            render: (rowData) => {
              if (!rowData?.splitAgentId) return " - ";
              return (
                <UserCommissionValue
                  userId={rowData.splitAgentId}
                  amount={rowData.splitAmount}
                />
              );
            },
          }),
        exportValue: (rowData) => formatCurrency({ value: rowData.splitAmount, userLocale: currentUser.locale }),
      },
      {
        name: "leaders",
        label: "app.commission.type.BONUS_LEADERSHIP_ONE_TIME",
        enable: true,
        exclusive: true,
        enableSort: false,
        definition: () =>
          newColumnConfig({
            title: "app.commission.type.BONUS_LEADERSHIP_ONE_TIME",
            field: "leaders",
            type: "string",
            align: "center",
            render: (rowData) => {
              const { leaders } = rowData ?? [];
              if (leaders.length === 0) return " - ";
              return (
                <LeadershipCommissionList
                  leaderships={leaders}
                  emptyLabel={"-"}
                />
              );
            },
          }),
        exportValue: (rowData) => {
          const leaders = rowData.leaders.filter((l) => l.levelName !== "CCO").map((l) => `${l.leadershipOneTimeCommission} (${l.userId})`);
          return leaders.length ? leaders.join(", ") : "-";
        },
      },
    ],
    [classes.link, classes.secondary, customerRoutePrefix, currentUser.id, currentUser.hideValues, currentUser.locale, shouldDisableAgentOrManagerLink, intl],
  );
};
